import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { fetchCashReconciliations, resolveCashReconciliation } from "@/api/finance";
import { PAYMENT_METHOD_LABELS } from "@/types/enums";
import { formatMoney, periodLabel } from "@/utils/format";

const STATUS_LABELS: Record<string, string> = {
  OPEN: "Расхождение",
  RESOLVED: "Закрыто",
};

export function CashReconciliationTab() {
  const queryClient = useQueryClient();
  const [reasonDraft, setReasonDraft] = useState<Record<string, string>>({});
  const { data, isLoading, error } = useQuery({
    queryKey: ["cash-reconciliation"],
    queryFn: () => fetchCashReconciliations(),
  });

  const resolveMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => resolveCashReconciliation(id, reason),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["cash-reconciliation"] }),
  });

  const rows = data ?? [];

  return (
    <QueryState isLoading={isLoading} error={error}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Филиал</TableCell>
            <TableCell>Период</TableCell>
            <TableCell>Способ</TableCell>
            <TableCell>По CRM</TableCell>
            <TableCell>По факту</TableCell>
            <TableCell>Расхождение</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell align="right">Действие</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.branchName}</TableCell>
              <TableCell>{periodLabel(row.period)}</TableCell>
              <TableCell>{PAYMENT_METHOD_LABELS[row.method]}</TableCell>
              <TableCell>{formatMoney(row.crmAmount)}</TableCell>
              <TableCell>{formatMoney(row.factAmount)}</TableCell>
              <TableCell sx={{ color: row.diff !== 0 ? "error.main" : "success.main" }}>{formatMoney(row.diff)}</TableCell>
              <TableCell>
                <StatusChip label={STATUS_LABELS[row.status] ?? row.status} />
              </TableCell>
              <TableCell align="right">
                {row.status === "OPEN" && (
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <TextField
                      size="small"
                      placeholder="Причина"
                      value={reasonDraft[row.id] ?? ""}
                      onChange={(e) => setReasonDraft((prev) => ({ ...prev, [row.id]: e.target.value }))}
                    />
                    <Button
                      size="small"
                      disabled={resolveMutation.isPending}
                      onClick={() => resolveMutation.mutate({ id: row.id, reason: reasonDraft[row.id] })}
                    >
                      Закрыть
                    </Button>
                  </Stack>
                )}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center">
                Расхождений по кассе нет.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </QueryState>
  );
}
